import { VStack } from '@chakra-ui/react';
import { useStableCallback } from '@main/shared/browser';
import { Children, createContext, PropsWithChildren, useMemo, useState } from 'react';

import { ActionClusterShell } from '../floating-action-buttons/action-buttons';
import { LiveMessage } from './live-message';

export type HoverButtonGroupProps = PropsWithChildren<{
  spacing?: string | number;
  forceHide?: boolean;
}>;

type SetIsVisible = (id: string, isVisible: boolean) => void;
export type HoverButtonGroupContextValue = {
  shortButtons: boolean;
  /** Inside the cluster the pill owns the edge, so a chip drops its own. */
  inCluster: boolean;
  setIsVisible: SetIsVisible;
  /**
   * Speaks a result politely. The pill is hidden whenever it is not
   * hovered or busy, which takes anything inside it out of the accessibility
   * tree, so the live region lives beside it. Absent outside a group.
   */
  announce?: (message: string) => void;
};

export const HoverButtonGroupContext = createContext<HoverButtonGroupContextValue>({
  shortButtons: false,
  inCluster: false,
  setIsVisible: () => undefined,
});

export function HoverButtonGroup({ spacing = '1.5', children, forceHide }: HoverButtonGroupProps) {
  const [isVisibleById, setIsVisibleById] = useState<Record<string, boolean>>({});
  const [message, setMessage] = useState('');

  const isVisible = useMemo(
    () => !forceHide && !Object.values(isVisibleById).every((x) => x === false),
    [forceHide, isVisibleById],
  );
  const setIsVisible = useStableCallback<SetIsVisible>((id, isVisible) =>
    setIsVisibleById((prev) => ({ ...prev, [id]: isVisible })),
  );
  const shortButtons = Children.toArray(children).length > 1;

  const contextValue = useMemo(
    () => ({ shortButtons, inCluster: true, setIsVisible, announce: setMessage }),
    [shortButtons, setIsVisible],
  );

  return (
    <HoverButtonGroupContext.Provider value={contextValue}>
      {/* Hidden with visibility, not display: the chips keep their box, so a
          label tooltip still closing after the pointer leaves stays anchored to
          its chip instead of jumping to the corner of the page. */}
      <VStack
        position="absolute"
        top={spacing}
        right={spacing}
        justifyContent="center"
        visibility={isVisible ? 'visible' : 'hidden'}
        _groupHover={{ visibility: forceHide ? 'hidden' : 'visible' }}
      >
        <ActionClusterShell>{children}</ActionClusterShell>
      </VStack>
      <LiveMessage message={message} onClear={() => setMessage('')} />
    </HoverButtonGroupContext.Provider>
  );
}

HoverButtonGroup.ParentProps = {
  position: 'relative',
  'data-group': true,
} as const;
